import { NotiExtraDomainType } from '@diritto/common/notification/constants';
import { InboxNotiDto } from '@diritto/common/notification/dtos/inbox-noti.dto';
import { NotiExtraDomainInfo } from '@diritto/common/notification/types/noti-info-types';
import { TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import styled from '@emotion/styled';
import { useTrackingState } from 'contexts/TrackingProvider';
import { ContentInboxNotiListItem } from 'app-components/inbox/items/ContentInboxNotiItem';
import { DirittoMessageInboxNotiItem } from 'app-components/inbox/items/DirittoMessageInboxNotiItem';
import { EpisodeInboxNotiItem } from 'app-components/inbox/items/EpisodeInboxNotiItem';
import { EmptyContentInboxNotiItem } from 'app-components/inbox/items/EmptyEpisodeInboxNotiItem';
import { ReplyInboxNotiListItem } from 'app-components/inbox/items/ReplyInboxNotiItem';
import { ReplyInboxLikeNotiListItem } from 'app-components/inbox/items/ReplyLikeInboxNotiItem';
import { ReReplyInboxNotiListItem } from 'app-components/inbox/items/ReReplyInboxNotiItem';
import { ReReplyInboxLikeNotiListItem } from 'app-components/inbox/items/ReReplyLikeInboxNotiItem';
import { RidiPointCouponInboxNotiListItem } from 'app-components/inbox/items/RidiPointCouponInboxNotiItem';
import { useCallback } from 'react';
import { EmptyList } from 'ui-components/common/EmptyList';
import { EpisodePublicRelayDto } from '@diritto/common/relay/dtos/episode-relay.dtos';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { Sad } from 'icons/ritto/Sad';

interface InboxNotiListProps {
  inboxNotifications: InboxNotiDto[];
  contents: Record<number, ContentPublicRelayDto>;
  episodes: Record<number, EpisodePublicRelayDto>;
}

export const InboxNotiList = ({ inboxNotifications, contents, episodes }: InboxNotiListProps) => {
  const { tracker } = useTrackingState();

  const handleClickItem = useCallback(
    (inboxNoti: InboxNotiDto) => {
      tracker.track(TrackingEventType.ClickInboxItem, {
        title: inboxNoti.title,
        sentAt: inboxNoti.sentAt,
        type: inboxNoti.notiExtraDomainType,
      });
    },
    [tracker],
  );

  const renderNotiListItem = useCallback(
    (inboxNoti: InboxNotiDto) => {
      switch (inboxNoti.notiExtraDomainType) {
        case NotiExtraDomainType.Episode: {
          const notiExtraDomainInfo = inboxNoti.notiExtraDomainInfo as NotiExtraDomainInfo[NotiExtraDomainType.Episode];
          const extraEpisodes = notiExtraDomainInfo.payload.ids
            .map(id => episodes[id])
            .filter(it => !!it) as EpisodePublicRelayDto[];
          const content = contents[extraEpisodes[0]?.contentId];

          if (!content || extraEpisodes.length === 0) {
            return <EmptyContentInboxNotiItem key={inboxNoti.id} inboxNoti={inboxNoti} />;
          }

          return (
            <EpisodeInboxNotiItem key={inboxNoti.id} inboxNoti={inboxNoti} content={content} episodes={extraEpisodes} />
          );
        }
        case NotiExtraDomainType.Content: {
          const notiExtraDomainInfo = inboxNoti.notiExtraDomainInfo as NotiExtraDomainInfo[NotiExtraDomainType.Content];
          const content = contents[notiExtraDomainInfo.payload.ids[0]];
          return content && <ContentInboxNotiListItem key={inboxNoti.id} inboxNoti={inboxNoti} content={content} />;
        }
        case NotiExtraDomainType.RidiPointCoupon: {
          const notiExtraDomainInfo =
            inboxNoti.notiExtraDomainInfo as NotiExtraDomainInfo[NotiExtraDomainType.RidiPointCoupon];
          return (
            <RidiPointCouponInboxNotiListItem
              key={inboxNoti.id}
              inboxNoti={inboxNoti}
              ridiPointCouponCode={notiExtraDomainInfo.payload.ridiPointCouponCode}
            />
          );
        }
        case NotiExtraDomainType.DirittoMessage: {
          const notiExtraDomainInfo =
            inboxNoti.notiExtraDomainInfo as NotiExtraDomainInfo[NotiExtraDomainType.DirittoMessage];
          return (
            <DirittoMessageInboxNotiItem
              key={inboxNoti.id}
              inboxNoti={inboxNoti}
              link={notiExtraDomainInfo.payload.deeplink}
              externalLink={notiExtraDomainInfo.payload.externalLink}
            />
          );
        }
        case NotiExtraDomainType.Reply: {
          const notiExtraDomainInfo = inboxNoti.notiExtraDomainInfo as NotiExtraDomainInfo[NotiExtraDomainType.Reply];
          return (
            <ReplyInboxNotiListItem
              key={inboxNoti.id}
              inboxNoti={inboxNoti}
              link={notiExtraDomainInfo.payload.deeplink}
              replyId={notiExtraDomainInfo.payload.replyId}
            />
          );
        }
        case NotiExtraDomainType.ReReply: {
          const notiExtraDomainInfo = inboxNoti.notiExtraDomainInfo as NotiExtraDomainInfo[NotiExtraDomainType.ReReply];
          return (
            <ReReplyInboxNotiListItem
              key={inboxNoti.id}
              inboxNoti={inboxNoti}
              link={notiExtraDomainInfo.payload.deeplink}
              replyId={notiExtraDomainInfo.payload.reReplyId}
            />
          );
        }
        case NotiExtraDomainType.ReplyLike: {
          const notiExtraDomainInfo =
            inboxNoti.notiExtraDomainInfo as NotiExtraDomainInfo[NotiExtraDomainType.ReplyLike];
          return (
            <ReplyInboxLikeNotiListItem
              key={inboxNoti.id}
              inboxNoti={inboxNoti}
              link={notiExtraDomainInfo.payload.deeplink}
            />
          );
        }
        case NotiExtraDomainType.ReReplyLike: {
          const notiExtraDomainInfo =
            inboxNoti.notiExtraDomainInfo as NotiExtraDomainInfo[NotiExtraDomainType.ReReplyLike];
          return (
            <ReReplyInboxLikeNotiListItem
              key={inboxNoti.id}
              inboxNoti={inboxNoti}
              link={notiExtraDomainInfo.payload.deeplink}
            />
          );
        }
        default: {
          return null;
        }
      }
    },
    [contents, episodes],
  );

  if (inboxNotifications.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[70vh] px-4">
        <EmptyList icon={<Sad />} description="알림이 없어요." />
      </div>
    );
  }

  return (
    <StyledList>
      {inboxNotifications.map(inboxNoti => (
        <StyledItem key={inboxNoti.id} onClick={() => handleClickItem(inboxNoti)}>
          {renderNotiListItem(inboxNoti)}
        </StyledItem>
      ))}
    </StyledList>
  );
};

const StyledList = styled.ul`
  margin-top: 8px;
`;

const StyledItem = styled.li`
  position: relative;
`;
